import { AppBar, Toolbar, Box, IconButton, Typography, Button, Container } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { site } from 'config/site';
import { motion, AnimatePresence } from 'framer-motion';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AppBar 
      position="fixed" 
      elevation={scrolled ? 4 : 0} 
      className="glass-header"
      component="header"
      sx={{ 
        background: scrolled ? 'rgba(11, 15, 25, 0.8)' : 'transparent',
        backdropFilter: scrolled ? 'blur(20px)' : 'none',
        borderBottom: scrolled ? '1px solid rgba(255, 255, 255, 0.05)' : 'none',
        transition: 'all 0.3s ease',
        pt: scrolled ? 0 : 2,
        pb: scrolled ? 0 : 2
      }}
    >
      <Container maxWidth="lg">
        <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between' }}>
          
          {/* Logo Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }} aria-label="Inicio">
              <motion.img 
                src={process.env.PUBLIC_URL + '/logos/base_icon_transparent_background.png'} 
                alt="Rolsys logo" 
                width="40" 
                height="40" 
                whileHover={{ scale: 1.05, rotate: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              />
              <Typography variant="h5" component="span" sx={{ ml: 1.5, color: '#F8FAFC', fontWeight: 800, letterSpacing: '-0.02em' }}>
                Rolsys Solutions<span style={{ color: '#3B82F6' }}>.</span>
              </Typography>
            </Link>
          </Box>

          {/* Desktop Navigation */}
          <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }} aria-label="Navegación principal">
            {site.navigation.map(n => {
              const isActive = location.pathname === n.path;
              return (
                <Button 
                  key={n.path} 
                  component={Link} 
                  to={n.path} 
                  sx={{ 
                    fontWeight: 600,
                    color: isActive ? '#3B82F6' : '#94A3B8',
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 4,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: isActive ? '20px' : '0',
                      height: '2px',
                      background: '#3B82F6',
                      borderRadius: '2px',
                      transition: 'width 0.3s ease'
                    },
                    '&:hover': {
                      color: '#F8FAFC',
                      background: 'rgba(255, 255, 255, 0.03)',
                      '&::after': { width: '20px' }
                    }
                  }}
                >
                  {n.label}
                </Button>
              );
            })}
            <Button 
              variant="contained" 
              color="primary"
              component={Link}
              to="/contacto"
              disableElevation
              sx={{ ml: 2, borderRadius: '8px', px: 3 }}
            >
              Contactar
            </Button>
          </Box>

          {/* Mobile Menu Toggle */}
          <IconButton 
            onClick={() => setOpen(!open)} 
            aria-label={open ? "Cerrar menú" : "Abrir menú"}
            sx={{ display: { xs: 'inline-flex', md: 'none' }, color: '#F8FAFC' }}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Toolbar>
      </Container>

      {/* Mobile Navigation Dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            style={{ overflow: 'hidden' }}
          >
            <Box component="nav" aria-label="Navegación móvil" sx={{ 
              display: { xs: 'flex', md: 'none' }, 
              flexDirection: 'column', 
              p: 3, 
              gap: 2, 
              background: 'rgba(11, 15, 25, 0.95)',
              backdropFilter: 'blur(20px)',
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
            }}>
              {site.navigation.map(n => {
                const isActive = location.pathname === n.path;
                return (
                  <Button 
                    key={n.path} 
                    component={Link} 
                    to={n.path} 
                    onClick={() => setOpen(false)} 
                    sx={{ 
                      justifyContent: 'flex-start',
                      color: isActive ? '#3B82F6' : '#F8FAFC',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      py: 1.5,
                      borderBottom: '1px solid rgba(255,255,255,0.05)'
                    }}
                  >
                    {n.label}
                  </Button>
                );
              })}
              <Button 
                variant="contained" 
                color="primary"
                component={Link}
                to="/contacto"
                onClick={() => setOpen(false)}
                disableElevation
                sx={{ mt: 2, py: 1.5, borderRadius: '8px' }}
              >
                Contactar
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </AppBar>
  );
}